import {
  IconButton,
  styled,
  Box,
  Stack,
  Divider,
  Tooltip,
  IconButtonProps,
} from "@mui/material";
import {
  MenuOpen,
  Menu,
  WebAssetOutlined,
  VerticalSplitOutlined,
  BackupTable,
  GrainOutlined,
  ExploreOutlined,
} from "@mui/icons-material";
import { usePtcldScriptContext } from "contexts/PtcldScriptContext";
import { useSearchParams } from "react-router-dom";
import { STATE_SELECTION } from "utils/potree";
import { ViewerMode } from "utils/misc";
import { object } from "yup";
import { useCallback, useMemo, useState } from "react";

interface ControlPanelButtonProps extends IconButtonProps {
  isActive?: boolean;
}

export const ControlPanelIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<ControlPanelButtonProps>(({ theme, isActive }) => ({
  color:
    theme.palette.mode === "dark"
      ? theme.palette.grey[400]
      : theme.palette.grey[700],
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(80, 150, 230, 0.15)"
        : "rgba(25, 118, 210, 0.08)",
    color:
      theme.palette.mode === "dark"
        ? theme.palette.primary.light
        : theme.palette.primary.main,
    transform: "scale(1.05)",
  },
  "&:active": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(80, 150, 230, 0.25)"
        : "rgba(25, 118, 210, 0.15)",
  },
  transition: "all 0.2s ease",
  position: "relative",
  margin: "3px 0",
  ...(isActive && {
    color:
      theme.palette.mode === "dark"
        ? theme.palette.primary.light
        : theme.palette.primary.main,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(80, 150, 230, 0.2)"
        : "rgba(25, 118, 210, 0.12)",
  }),
}));

interface ControlPanelProps {
  viewerRef: React.RefObject<Potree.Viewer | null>;
}

const ControlPanel = ({ viewerRef }: ControlPanelProps) => {
  const {
    ifcLoading,
    scriptsLoaded,
    isSplit,
    setIsSplit,
    setIsLock,
    setIsOverlay,
    width,
    setWidth,
    menu,
    setMenuOpen,
    isIfcAvailable,
    isObjAvailable,
  } = usePtcldScriptContext();
  const [isClassificationActive, setIsClassificationActive] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const selection = searchParams.get("selection");
  const segmentation = searchParams.get("segmentation");
  const mode = searchParams.get("mode") || "uniscan";

  const ifcHandler = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (mode === ViewerMode.UNISCAN) {
      setIsSplit(!isSplit);
      setIsLock(true);
      if (!isSplit && viewerRef.current) {
        viewerRef.current.splitPaneResize(width);
        newSearchParams.set("selection", STATE_SELECTION.ifc);
        setSearchParams(newSearchParams);
      } else if (isSplit && viewerRef.current) {
        newSearchParams.delete("selection");
        setSearchParams(newSearchParams);
      }
    }
  }, [searchParams, setSearchParams]);

  const objHandler = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (mode === ViewerMode.UNISCAN) {
      setIsSplit(!isSplit);
      setIsLock(true);
      if (!isSplit && viewerRef.current) {
        newSearchParams.set("selection", STATE_SELECTION.obj);
        setSearchParams(newSearchParams);
      } else if (isSplit && viewerRef.current) {
        newSearchParams.delete("selection");
        setSearchParams(newSearchParams);
      }
    }
  }, [searchParams, setSearchParams]);

  const classificationHandler = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    const currentAttribute =
      viewerRef.current?.scene.pointclouds?.[0]?.material?.activeAttributeName;
    if (currentAttribute !== "classification") {
      newSearchParams.set("segmentation", "true");
      setSearchParams(newSearchParams);
      viewerRef.current?.scene.pointclouds.forEach((pointcloud) => {
        pointcloud.material.activeAttributeName = "classification";
      });
    } else {
      newSearchParams.delete("segmentation");
      setSearchParams(newSearchParams);
      viewerRef.current?.scene.pointclouds.forEach((pointcloud) => {
        pointcloud.material.activeAttributeName = "rgba";
      });
    }
  }, [searchParams, setSearchParams]);

  return (
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        left: {
          xs: menu ? "280px" : "10px",
          sm: menu ? "350px" : "10px",
        },
        transition: "all 0.4s ease",
        zIndex: 1200,
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? "background.default"
            : "rgba(240, 245, 250, 0.85)",
        backdropFilter: "blur(8px)",
        borderRadius: 3,
        border: (theme) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(255, 255, 255, 0.25)",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 6px 24px rgba(0, 0, 0, 0.35)"
            : "0 6px 24px rgba(0, 0, 0, 0.15)",
        overflow: "hidden",
        maxWidth: "fit-content",
      }}
    >
      <Stack
        direction="column"
        sx={{
          p: 1,
          "& .controlGroup": {
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            my: 0.3,
          },
          "& .controlDivider": {
            width: "85%",
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.08)",
            my: 0.3,
          },
        }}
      >
        <Box className="controlGroup">
          <Tooltip
            title={menu ? "Close Menu" : "Open Menu"}
            placement="right"
            arrow
          >
            <ControlPanelIconButton
              isActive={menu}
              onClick={() => {
                setMenuOpen(!menu);
              }}
              size="medium"
            >
              {menu ? (
                <MenuOpen sx={{ fontSize: "1.5rem" }} />
              ) : (
                <Menu sx={{ fontSize: "1.5rem" }} />
              )}
            </ControlPanelIconButton>
          </Tooltip>
        </Box>

        {(isIfcAvailable || isObjAvailable) && mode === ViewerMode.UNISCAN && (
          <Divider flexItem className="controlDivider" />
        )}
        <Box className="controlGroup">
          {isIfcAvailable && mode === ViewerMode.UNISCAN && (
            <>
              <Tooltip
                title={
                  isSplit && selection === STATE_SELECTION.ifc
                    ? "Full Screen View"
                    : "Split IFC View"
                }
                placement="right"
                arrow
              >
                <ControlPanelIconButton
                  isActive={selection === STATE_SELECTION.ifc}
                  onClick={ifcHandler}
                  size="medium"
                >
                  <VerticalSplitOutlined sx={{ fontSize: "1.5rem" }} />
                </ControlPanelIconButton>
              </Tooltip>
            </>
          )}

          {isObjAvailable && mode === ViewerMode.UNISCAN && (
            <>
              <Tooltip
                title={
                  isSplit && selection === STATE_SELECTION.obj
                    ? "Full Screen View"
                    : "Split Floor Plan View"
                }
                placement="right"
                arrow
              >
                <ControlPanelIconButton
                  isActive={selection === STATE_SELECTION.obj}
                  onClick={objHandler}
                  size="medium"
                >
                  <BackupTable sx={{ fontSize: "1.5rem" }} />
                </ControlPanelIconButton>
              </Tooltip>
            </>
          )}
        </Box>

        <Divider flexItem className="controlDivider" />

        <Box className="controlGroup">
          <Tooltip title="Toggle Point Cloud Colors" placement="right" arrow>
            <ControlPanelIconButton
              onClick={classificationHandler}
              isActive={!!segmentation}
              size="medium"
            >
              <GrainOutlined sx={{ fontSize: "1.5rem" }} />
            </ControlPanelIconButton>
          </Tooltip>

          <Tooltip title="Toggle Map View" placement="right" arrow>
            <ControlPanelIconButton
              onClick={() => {
                viewerRef.current?.toggleMap();
              }}
              size="medium"
            >
              <ExploreOutlined sx={{ fontSize: "1.5rem" }} />
            </ControlPanelIconButton>
          </Tooltip>
        </Box>
      </Stack>

      {/* Active status indicator with subtle glow */}
      {(isSplit || menu) && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            backgroundColor: (theme) =>
              isSplit
                ? theme.palette.primary.main
                : theme.palette.secondary.main,
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? `0 -1px 6px ${isSplit ? theme.palette.primary.dark : theme.palette.secondary.dark}`
                : `0 -1px 4px ${isSplit ? "rgba(25, 118, 210, 0.3)" : "rgba(156, 39, 176, 0.3)"}`,
          }}
        />
      )}
    </Box>
  );
};

export default ControlPanel;
